<script setup lang="ts">
/**
 * 방문요양 임금대장 엑셀 다운로드 컴포넌트
 * - caion_wage_home_data (cwhd_*) 필드 기반
 */
import { ref, computed } from 'vue';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { compareJobThenName } from '@/policies/jobTypeSort';
import { formatDurationHoursMinutes } from '@/composables/wage/useWageCalculator';
import {
  dbWageAmount,
  getHomeWageLedgerNetPay,
  getHomeWageLedgerAccountingGrossPay,
  sumHomeWageAccountingDisplayPayParts,
  getHomeWageCaregiverSlipPayDisplay,
  getHomeWageAdminSettingDisplayParts,
  calcHomeWageClientReportGrossPay,
  buildHomeWageClientReportRowFromDb,
  resolveHomeWageSevereDisplayAmount,
  resolveHomeWageDisplayRetirementAmount,
  isHomeWageLaborDayMonth,
  hasHomeWageStoredCtParts,
  readHomeWageStoredCtParts,
  sumHomeWageStoredCtPayParts,
  HOME_WAGE_NONTAX_TERM,
} from '@/policies';

const showModal = ref(false);
const isDownloading = ref(false);
const reportType = ref<'accounting' | 'client'>('accounting');

/** 급여대장 데이터 Row (API 응답 형태) */
interface HomeWageListRow {
  idx?: number;
  m_code?: string;
  bizGubun?: string;
  yearMonth?: string;
  payDate?: string;
  payCalcHourly?: number;
  name?: string;
  birth?: string;
  job?: string;
  basic?: number;
  weekend?: number;
  annual?: number;
  laborDay?: number;
  overtime?: number;
  overtimeNontax?: number;
  night?: number;
  nightNontax?: number;
  holiday?: number;
  holidayNontax?: number;
  senior?: number;
  sunim?: number;
  bosu?: number;
  severe?: number;
  lunch?: number;
  lunchDirector?: number;
  bonus?: number;
  car?: number;
  child?: number;
  welfare?: number;
  other?: number;
  wageTotal?: number;
  l_yeon?: number;
  l_gun?: number;
  l_jang?: number;
  l_go?: number;
  l_so?: number;
  l_ji?: number;
  l_jiwon?: number;
  l_bonin?: number;
  l_etc?: number;
  l_total?: number;
  c_yeon?: number;
  c_gun?: number;
  c_jang?: number;
  c_go?: number;
  c_sil?: number;
  c_san?: number;
  c_out?: number;
  c_total?: number;
  net?: number;
  grossPay?: number;
  nonTax?: number;
  l_save?: number;
  c_save?: number;
  profit?: number;
  svc?: string;
  cnt?: string;
  hour?: string;
  days?: string;
  memo?: string;
  overtimeHours?: number;
  nightHours?: number;
  holidayHours?: number;
  payOvertime?: string | boolean | null;
  payHoliday?: string | boolean | null;
  payNight?: string | boolean | null;
  productionNontax?: string | boolean | null;
}

interface Props {
  year: number;
  month: number;
  wageData: HomeWageListRow[];
  memberName?: string;
}

const props = defineProps<Props>();

/** 귀속 연월 (YYYYMM) */
const reportYearMonth = computed(
  () => `${props.year}${String(props.month).padStart(2, '0')}`
);

/** 노동절수당 — 5월 급여에만 표시 */
const isLaborDayReportMonth = computed(() =>
  isHomeWageLaborDayMonth(reportYearMonth.value)
);

/** 5월 외·DB 레거시값 무시 */
const getDisplayLaborDay = (row: HomeWageListRow) => {
  if (!isLaborDayReportMonth.value) return 0;
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.laborDay : dbWageAmount(row.laborDay);
  }
  return getAdminParts(row).laborDay;
};

/** 센터장·일반임금대장 — slip */
const toClientReportRow = (row: HomeWageListRow) =>
  buildHomeWageClientReportRowFromDb(row as Record<string, unknown>);

const getStoredCt = (row: HomeWageListRow) => {
  const rec = row as Record<string, unknown>;
  if (!hasHomeWageStoredCtParts(rec)) return null;
  return readHomeWageStoredCtParts(rec, reportYearMonth.value);
};

const getClientSlipPayDisplay = (row: HomeWageListRow) =>
  getHomeWageCaregiverSlipPayDisplay(toClientReportRow(row));

const getClientAllowance = (
  row: HomeWageListRow,
  key: 'senior' | 'sunim' | 'bosu' | 'bonus' | 'car' | 'child' | 'welfare' | 'other'
) => {
  const ct = getStoredCt(row);
  if (ct) return ct[key];
  return dbWageAmount(row[key]);
};

const isClientReport = () => reportType.value === 'client';

/** 회계 — 관리자 시점 임금설정과 동일 칸 */
const getAdminParts = (row: HomeWageListRow) =>
  getHomeWageAdminSettingDisplayParts(
    row as Record<string, unknown>,
    reportYearMonth.value
  );

/**
 * 일반(센터장): slip — 중식→연차/기본급
 * 회계(관리자): 관리자 시점 임금설정과 동일
 */
const getDisplayBasic = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.basic : getClientSlipPayDisplay(row).basic;
  }
  return getAdminParts(row).basic;
};

const getDisplayWeekend = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.weekend : getClientSlipPayDisplay(row).weekend;
  }
  return getAdminParts(row).weekend;
};

const getDisplayAnnual = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.annual : getClientSlipPayDisplay(row).annual;
  }
  return getAdminParts(row).annual;
};

const getDisplayHoliday = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.holiday : getClientSlipPayDisplay(row).holiday;
  }
  return getAdminParts(row).holiday + getAdminParts(row).holidayNontax;
};

const getDisplayNight = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.night : getClientSlipPayDisplay(row).night;
  }
  return getAdminParts(row).night + getAdminParts(row).nightNontax;
};

const getDisplayOvertimeTotal = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.overtime : getClientSlipPayDisplay(row).overtime;
  }
  return getAdminParts(row).overtime + getAdminParts(row).overtimeNontax;
};

const getDisplaySevere = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.severe : resolveHomeWageSevereDisplayAmount(toClientReportRow(row));
  }
  return getAdminParts(row).severe;
};

/** 고객용 — slip 중식 / 회계 — 자동중식 + 센터장 입력 중식 합산 */
const getDisplayLunch = (row: HomeWageListRow) => {
  if (isClientReport()) {
    const ct = getStoredCt(row);
    return ct ? ct.lunch : getClientSlipPayDisplay(row).lunch;
  }
  const p = getAdminParts(row);
  return p.lunchAuto + p.lunchDirector;
};

/** 회계 — 관리자 시점 과세/비과세 */
const getAccountingOvertimeTax = (row: HomeWageListRow) => getAdminParts(row).overtime;
const getAccountingOvertimeNontax = (row: HomeWageListRow) => getAdminParts(row).overtimeNontax;
const getAccountingNightTax = (row: HomeWageListRow) => getAdminParts(row).night;
const getAccountingNightNontax = (row: HomeWageListRow) => getAdminParts(row).nightNontax;
const getAccountingHolidayTax = (row: HomeWageListRow) => getAdminParts(row).holiday;
const getAccountingHolidayNontax = (row: HomeWageListRow) => getAdminParts(row).holidayNontax;
const getAccountingPremiumNontax = (row: HomeWageListRow) =>
  getAccountingOvertimeNontax(row) + getAccountingNightNontax(row) + getAccountingHolidayNontax(row);
const getAccountingPremiumTax = (row: HomeWageListRow) =>
  getAccountingOvertimeTax(row) + getAccountingNightTax(row) + getAccountingHolidayTax(row);

/** 퇴직금 — 실비변상 식대 차감 산식 (저장 c_out 구값 보정) */
const getReportRetirement = (row: HomeWageListRow) =>
  resolveHomeWageDisplayRetirementAmount(
    row as Record<string, unknown>,
    reportYearMonth.value
  );

/** 기관공제합계 — 보험 항목 + 표시용 퇴직금 */
const getReportCorpTotal = (row: HomeWageListRow) =>
  dbWageAmount(row.c_yeon)
  + dbWageAmount(row.c_gun)
  + dbWageAmount(row.c_jang)
  + dbWageAmount(row.c_go)
  + dbWageAmount(row.c_sil)
  + dbWageAmount(row.c_san)
  + getReportRetirement(row);

const getDisplayWageField = (row: HomeWageListRow, field: string): number => {
  switch (field) {
    case 'basic':
      return getDisplayBasic(row);
    case 'weekend':
      return getDisplayWeekend(row);
    case 'annual':
      return getDisplayAnnual(row);
    case 'laborDay':
      return getDisplayLaborDay(row);
    case 'overtime':
      return isClientReport() ? getDisplayOvertimeTotal(row) : getAccountingOvertimeTax(row);
    case 'overtimeNontax':
      return getAccountingOvertimeNontax(row);
    case 'night':
      return isClientReport() ? getDisplayNight(row) : getAccountingNightTax(row);
    case 'nightNontax':
      return getAccountingNightNontax(row);
    case 'holiday':
      return isClientReport() ? getDisplayHoliday(row) : getAccountingHolidayTax(row);
    case 'holidayNontax':
      return getAccountingHolidayNontax(row);
    case 'premiumNontax':
      return getAccountingPremiumNontax(row);
    case 'premiumTax':
      return getAccountingPremiumTax(row);
    case '_overtime_total':
      return getDisplayOvertimeTotal(row);
    case '_night_total':
      return getDisplayNight(row);
    case '_holiday_total':
      return getDisplayHoliday(row);
    case 'severe':
      return getDisplaySevere(row);
    case 'lunch':
      return getDisplayLunch(row);
    case 'sunim':
      return isClientReport()
        ? getClientAllowance(row, 'sunim')
        : getAdminParts(row).sunim;
    case 'c_out':
      return getReportRetirement(row);
    case 'c_total':
      return getReportCorpTotal(row);
    default:
      if (isClientReport()) {
        const ct = getStoredCt(row);
        if (ct && field in ct) {
          return ct[field as keyof typeof ct] as number;
        }
      }
      return dbWageAmount((row as Record<string, unknown>)[field] as number);
  }
};

const emit = defineEmits<{
  (e: 'error', message: string): void;
}>();

// 만나이 계산
const calculateAge = (birthDateStr: string | undefined, year: number, month: number) => {
  if (!birthDateStr) return '';
  const birthDate = new Date(birthDateStr);
  const baseDate = new Date(year, month - 1, 1);
  let age = baseDate.getFullYear() - birthDate.getFullYear();
  if (baseDate.getMonth() < birthDate.getMonth() || 
      (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age > 0 ? `만${age}세` : '';
};

const formatReportFileName = (
  memberName: string | undefined,
  year: number,
  month: number,
  kind: 'client' | 'accounting' = 'client'
) => {
  const facility = (memberName || '시설').trim().replace(/[\\/:*?"<>|]/g, '_') || '시설';
  const mm = String(month).padStart(2, '0');
  if (kind === 'accounting') {
    return `${facility}_${year}년${mm}월_회계처리용_임금대장.xlsx`;
  }
  return `${facility}_${year}년${mm}월_임금대장.xlsx`;
};

// 숫자 포맷팅
const formatNumber = (num: number | undefined | null) => {
  const n = Number(num) || 0;
  return n.toLocaleString();
};

const formatWorkHoursCell = (hour?: string | number | null) =>
  formatDurationHoursMinutes(hour) || String(hour ?? '') || '0';

/** 회계 — 관리자 시점 합계 / 일반 — 센터장 slip 합계 */
const getRowGrossPay = (row: HomeWageListRow) =>
  getHomeWageLedgerAccountingGrossPay(
    row as unknown as Record<string, unknown>,
    reportYearMonth.value
  );

const getClientRowGrossPay = (row: HomeWageListRow) => {
  const ct = getStoredCt(row);
  if (ct) return sumHomeWageStoredCtPayParts(ct);
  return calcHomeWageClientReportGrossPay(toClientReportRow(row), reportYearMonth.value);
};

const getReportRowGrossPay = (row: HomeWageListRow) =>
  isClientReport() ? getClientRowGrossPay(row) : getRowGrossPay(row);

const isAmountClose = (a: number, b: number) => Math.abs(a - b) <= 1;

/** 엑셀 합계 키 — 근로자/기관 국민연금처럼 라벨이 같아도 컬럼을 구분 */
const excelColTotalKey = (col: { field?: string | null; name: string }) =>
  col.field || col.name;

/** 시트 맨 위 제목 */
const wageLedgerTitle = () => {
  const facility = (props.memberName || '').trim();
  const period = `${props.year}년 ${props.month}월 임금대장`;
  return facility ? `${facility} ${period}` : period;
};

const addWageLedgerTitleRow = (worksheet: ExcelJS.Worksheet, title: string, colCount: number) => {
  const titleRow = worksheet.addRow([title]);
  titleRow.height = 32;
  const lastCol = Math.max(colCount, 1);
  for (let i = 1; i <= lastCol; i++) {
    const cell = titleRow.getCell(i);
    cell.font = { bold: true, size: 16, color: { argb: 'FF1e3a5f' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  }
  if (lastCol > 1) {
    worksheet.mergeCells(1, 1, 1, lastCol);
  }
};

/** 미리보기 항목 합 — 해당 시점 표시 칸 기준 (항목합=합계) */
const sumPreviewWageColumns = (row: HomeWageListRow) => {
  if (!isClientReport()) {
    // 회계: 정책 단일 소스 (표시 칸 합 = 임금합계)
    return sumHomeWageAccountingDisplayPayParts(
      row as unknown as Record<string, unknown>,
      reportYearMonth.value
    );
  }
  const ct = getStoredCt(row);
  return (
    getDisplayBasic(row) +
    getDisplayWeekend(row) +
    getDisplayAnnual(row) +
    getDisplayLaborDay(row) +
    getDisplayOvertimeTotal(row) +
    getDisplayNight(row) +
    getDisplayHoliday(row) +
    (ct ? ct.senior : dbWageAmount(row.senior)) +
    (ct ? ct.sunim : dbWageAmount(row.sunim)) +
    (ct ? ct.bosu : dbWageAmount(row.bosu)) +
    getDisplaySevere(row) +
    (ct ? ct.bonus : dbWageAmount(row.bonus)) +
    getDisplayLunch(row) +
    (ct ? ct.car : dbWageAmount(row.car)) +
    (ct ? ct.child : dbWageAmount(row.child)) +
    (ct ? ct.welfare : dbWageAmount(row.welfare)) +
    (ct ? ct.other : dbWageAmount(row.other))
  );
};

/** 미리보기 임금합계 불일치 — 화면 항목 합 ≠ 임금합계 칸 */
const rowWageMismatch = (row: HomeWageListRow) => {
  const displayed = getReportRowGrossPay(row);
  const parts = sumPreviewWageColumns(row);
  if (parts === 0 && displayed === 0) return false;
  return !isAmountClose(parts, displayed);
};

const sumLaborDeductParts = (row: HomeWageListRow) =>
  dbWageAmount(row.l_yeon) +
  dbWageAmount(row.l_gun) +
  dbWageAmount(row.l_jang) +
  dbWageAmount(row.l_go) +
  dbWageAmount(row.l_so) +
  dbWageAmount(row.l_ji) +
  dbWageAmount(row.l_jiwon) +
  dbWageAmount(row.l_bonin) +
  dbWageAmount(row.l_etc);

const sumCorpDeductParts = (row: HomeWageListRow) =>
  dbWageAmount(row.c_yeon) +
  dbWageAmount(row.c_gun) +
  dbWageAmount(row.c_jang) +
  dbWageAmount(row.c_go) +
  dbWageAmount(row.c_sil) +
  dbWageAmount(row.c_san) +
  getReportRetirement(row);

const rowLaborMismatch = (row: HomeWageListRow) =>
  !isAmountClose(sumLaborDeductParts(row), dbWageAmount(row.l_total));

const rowCorpMismatch = (row: HomeWageListRow) =>
  !isAmountClose(sumCorpDeductParts(row), getReportCorpTotal(row));

const footerMismatch = computed(() => {
  const t = totals.value;
  const anyRowWage = sortedData.value.some((r) => rowWageMismatch(r));
  const anyRowLabor = sortedData.value.some((r) => rowLaborMismatch(r));
  const anyRowCorp = sortedData.value.some((r) => rowCorpMismatch(r));
  const previewPartsSum = sortedData.value.reduce(
    (s, r) => s + sumPreviewWageColumns(r),
    0
  );
  return {
    wage: anyRowWage || !isAmountClose(previewPartsSum, t.grossPay),
    labor: anyRowLabor,
    corp: anyRowCorp,
  };
});

/** 실수령액 = 표시 지급금액 − 근로자공제 (DB cwhd_net 레거시 보정) */
const getRowNetPay = (row: HomeWageListRow) =>
  getHomeWageLedgerNetPay(row as Record<string, unknown>, {
    grossPay: getReportRowGrossPay(row),
    yearMonth: reportYearMonth.value,
    reportType: reportType.value,
  });

const hasDisplayLunch = () =>
  (props.wageData?.some((row) => getDisplayLunch(row) > 0) ?? false);

const reportModalTitle = computed(() => {
  const prefix = reportType.value === 'accounting' ? '회계처리용 ' : '';
  return `${props.memberName || '시설'} ${prefix}임금대장 미리보기 (${props.year}년 ${props.month}월)`;
});

// 정렬된 데이터
const sortedData = computed(() => {
  if (!props.wageData) return [];
  return [...props.wageData].sort((a, b) =>
    compareJobThenName(a.job, a.name, b.job, b.name)
  );
});

// 합계 계산
const totals = computed(() => {
  const t = {
    basic: 0, weekend: 0, annual: 0, laborDay: 0,
    overtime: 0, overtimeNontax: 0, night: 0, nightNontax: 0, holiday: 0, holidayNontax: 0,
    premiumTax: 0, premiumNontax: 0,
    overtimeTotal: 0, nightTotal: 0, holidayTotal: 0,
    senior: 0, sunim: 0, bosu: 0, severe: 0,
    bonus: 0, lunch: 0, car: 0, child: 0, welfare: 0, other: 0,
    wageTotal: 0, nonTax: 0, grossPay: 0,
    lYeon: 0, lGun: 0, lJang: 0, lGo: 0, lSo: 0, lJi: 0, lJiwon: 0, lBonin: 0, lEtc: 0, lTotal: 0,
    cYeon: 0, cGun: 0, cJang: 0, cGo: 0, cSil: 0, cSan: 0, cOut: 0, cTotal: 0,
    netPay: 0
  };
  sortedData.value.forEach(row => {
    t.basic += getDisplayBasic(row);
    t.weekend += getDisplayWeekend(row);
    t.annual += getDisplayAnnual(row);
    t.laborDay += getDisplayLaborDay(row);
    t.overtime += getAccountingOvertimeTax(row);
    t.overtimeNontax += getAccountingOvertimeNontax(row);
    t.night += getAccountingNightTax(row);
    t.nightNontax += getAccountingNightNontax(row);
    t.holiday += getAccountingHolidayTax(row);
    t.holidayNontax += getAccountingHolidayNontax(row);
    t.premiumTax += getAccountingPremiumTax(row);
    t.premiumNontax += getAccountingPremiumNontax(row);
    t.overtimeTotal += getDisplayOvertimeTotal(row);
    t.nightTotal += getDisplayNight(row);
    t.holidayTotal += getDisplayHoliday(row);
    t.senior += isClientReport() ? getClientAllowance(row, 'senior') : getAdminParts(row).senior;
    t.sunim += isClientReport() ? getClientAllowance(row, 'sunim') : getAdminParts(row).sunim;
    t.bosu += isClientReport() ? getClientAllowance(row, 'bosu') : getAdminParts(row).bosu;
    t.severe += getDisplaySevere(row);
    t.bonus += isClientReport() ? getClientAllowance(row, 'bonus') : getAdminParts(row).bonus;
    t.lunch += getDisplayLunch(row);
    t.car += isClientReport() ? getClientAllowance(row, 'car') : getAdminParts(row).car;
    t.child += isClientReport() ? getClientAllowance(row, 'child') : getAdminParts(row).child;
    t.welfare += isClientReport() ? getClientAllowance(row, 'welfare') : getAdminParts(row).welfare;
    t.other += isClientReport() ? getClientAllowance(row, 'other') : getAdminParts(row).other;
    t.wageTotal += dbWageAmount(row.wageTotal);
    t.nonTax += dbWageAmount(row.nonTax);
    t.grossPay += getReportRowGrossPay(row);
    t.lYeon += dbWageAmount(row.l_yeon);
    t.lGun += dbWageAmount(row.l_gun);
    t.lJang += dbWageAmount(row.l_jang);
    t.lGo += dbWageAmount(row.l_go);
    t.lSo += dbWageAmount(row.l_so);
    t.lJi += dbWageAmount(row.l_ji);
    t.lJiwon += dbWageAmount(row.l_jiwon);
    t.lBonin += dbWageAmount(row.l_bonin);
    t.lEtc += dbWageAmount(row.l_etc);
    t.lTotal += dbWageAmount(row.l_total);
    t.cYeon += dbWageAmount(row.c_yeon);
    t.cGun += dbWageAmount(row.c_gun);
    t.cJang += dbWageAmount(row.c_jang);
    t.cGo += dbWageAmount(row.c_go);
    t.cSil += dbWageAmount(row.c_sil);
    t.cSan += dbWageAmount(row.c_san);
    t.cOut += getReportRetirement(row);
    t.cTotal += getReportCorpTotal(row);
    t.netPay += getRowNetPay(row);
  });
  return t;
});

// 0원이 아닌 값이 있는지 확인
const hasNonZero = (field: keyof HomeWageListRow): boolean => {
  if (!props.wageData) return false;
  return props.wageData.some(row => (row[field] as number) > 0);
};

// 컬럼 표시 여부 — 표시 금액(slip) 기준
const showColumns = computed(() => ({
  weekend: props.wageData?.some((row) => getDisplayWeekend(row) > 0) ?? false,
  annual: props.wageData?.some((row) => getDisplayAnnual(row) > 0) ?? false,
  laborDay: isLaborDayReportMonth.value && props.wageData.some((row) => getDisplayLaborDay(row) > 0),
  premiumTax: props.wageData?.some((row) => getAccountingPremiumTax(row) > 0) ?? false,
  premiumNontax: props.wageData?.some((row) => getAccountingPremiumNontax(row) > 0) ?? false,
  senior: hasNonZero('senior'),
  sunim: props.wageData?.some((row) =>
    (isClientReport() ? getClientAllowance(row, 'sunim') : getAdminParts(row).sunim) > 0
  ) ?? false,
  bosu: hasNonZero('bosu'),
  severe: props.wageData?.some((row) => getDisplaySevere(row) > 0) ?? false,
  bonus: hasNonZero('bonus'),
  lunch: hasDisplayLunch(),
  car: hasNonZero('car'),
  child: hasNonZero('child'),
  welfare: hasNonZero('welfare'),
  other: hasNonZero('other'),
  // 근로자공제
  lYeon: hasNonZero('l_yeon'),
  lGun: hasNonZero('l_gun'),
  lJang: hasNonZero('l_jang'),
  lGo: hasNonZero('l_go'),
  lSo: hasNonZero('l_so'),
  lJi: hasNonZero('l_ji'),
  lJiwon: hasNonZero('l_jiwon'),
  lBonin: hasNonZero('l_bonin'),
  lEtc: hasNonZero('l_etc'),
  // 기관공제
  cYeon: hasNonZero('c_yeon'),
  cGun: hasNonZero('c_gun'),
  cJang: hasNonZero('c_jang'),
  cGo: hasNonZero('c_go'),
  cSil: hasNonZero('c_sil'),
  cSan: hasNonZero('c_san'),
  cOut: props.wageData?.some((row) => getReportRetirement(row) > 0) ?? false,
}));

const showClientColumns = computed(() => ({
  overtimeTotal: props.wageData?.some((r) => getDisplayOvertimeTotal(r) > 0) ?? false,
  nightTotal: props.wageData?.some((r) => getDisplayNight(r) > 0) ?? false,
  holidayTotal: props.wageData?.some((r) => getDisplayHoliday(r) > 0) ?? false,
}));

/** 미리보기 합계행 — 표시 금액(slip) 기준 */
const displayBasicTotal = computed(() => totals.value.basic);
const displayAnnualTotal = computed(() => totals.value.annual);
const displayHolidayTotal = computed(() => totals.value.holidayTotal);
const displayNightTotal = computed(() => totals.value.nightTotal);
const displayOvertimeTotal = computed(() => totals.value.overtimeTotal);

// 동적 colspan 계산
const wageColspan = computed(() => {
  let count = 2; // 기본급, 임금합계 (필수)
  const s = showColumns.value;
  const sc = showClientColumns.value;

  if (reportType.value === 'client') {
    if (sc.overtimeTotal) count++;
    if (sc.nightTotal) count++;
    if (sc.holidayTotal) count++;
  } else {
    if (s.premiumTax) count++;
    if (s.premiumNontax) count++;
  }
  if (s.weekend) count++;
  if (s.annual) count++;
  if (s.laborDay) count++;
  if (s.senior) count++;
  if (s.sunim) count++;
  if (s.bosu) count++;
  if (s.severe) count++;
  if (s.bonus) count++;
  if (s.lunch) count++;
  if (s.car) count++;
  if (s.child) count++;
  if (s.welfare) count++;
  if (s.other) count++;
  return count;
});

const empDeductColspan = computed(() => {
  let count = 1; // 공제합계 (필수)
  const s = showColumns.value;
  if (s.lYeon) count++;
  if (s.lGun) count++;
  if (s.lJang) count++;
  if (s.lGo) count++;
  if (s.lSo) count++;
  if (s.lJi) count++;
  if (s.lJiwon) count++;
  if (s.lBonin) count++;
  if (s.lEtc) count++;
  return count;
});

const corpDeductColspan = computed(() => {
  let count = 1; // 기관합계 (필수)
  const s = showColumns.value;
  if (s.cYeon) count++;
  if (s.cGun) count++;
  if (s.cJang) count++;
  if (s.cGo) count++;
  if (s.cSil) count++;
  if (s.cSan) count++;
  if (s.cOut) count++;
  return count;
});

// 미리보기 열기
const openPreview = (type: 'accounting' | 'client' = 'accounting') => {
  reportType.value = type;
  showModal.value = true;
};

// 미리보기 닫기
const closePreview = () => {
  showModal.value = false;
};

// 다운로드
const handleDownload = () => {
  if (reportType.value === 'client') {
    downloadClientReport();
  } else {
    downloadPayrollReport();
  }
};

// 임금대장 엑셀 다운로드
const downloadPayrollReport = async () => {
  if (!props.wageData || props.wageData.length === 0) {
    emit('error', '다운로드할 데이터가 없습니다.');
    return;
  }

  isDownloading.value = true;
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('임금대장');

    type ColDef = { group: string; name: string; field: string | null; required?: boolean };
    const allColumns: ColDef[] = [
      // 기본정보 (필수)
      { group: 'basic', name: '순번', field: null, required: true },
      { group: 'basic', name: '성명', field: null, required: true },
      { group: 'basic', name: '생년월일', field: null, required: true },
      { group: 'basic', name: '직종', field: null, required: true },
      // 근로정보 (필수)
      { group: 'work', name: '시급', field: 'payCalcHourly', required: true },
      { group: 'work', name: '일수', field: 'days', required: true },
      { group: 'work', name: '시간', field: 'hour', required: true },
      // 임금구성
      { group: 'wage', name: '기본급', field: 'basic', required: true },
      { group: 'wage', name: '주휴수당', field: 'weekend' },
      { group: 'wage', name: '연차수당', field: 'annual' },
      { group: 'wage', name: '노동절수당(비과세)', field: 'laborDay' },
      { group: 'wage', name: '연야휴과세', field: 'premiumTax' },
      { group: 'wage', name: '연야휴비과세', field: 'premiumNontax' },
      { group: 'wage', name: '장기근속', field: 'senior' },
      { group: 'wage', name: '선임수당', field: 'sunim' },
      { group: 'wage', name: '보수교육', field: 'bosu' },
      { group: 'wage', name: '중증가산', field: 'severe' },
      { group: 'wage', name: '상여금', field: 'bonus' },
      { group: 'wage', name: HOME_WAGE_NONTAX_TERM.lunch, field: 'lunch' },
      { group: 'wage', name: HOME_WAGE_NONTAX_TERM.car, field: 'car' },
      { group: 'wage', name: HOME_WAGE_NONTAX_TERM.child, field: 'child' },
      { group: 'wage', name: '처우개선', field: 'welfare' },
      { group: 'wage', name: '기타수당', field: 'other' },
      { group: 'wage', name: '임금합계', field: null, required: true },
      // 근로자공제
      { group: 'empDeduct', name: '국민연금', field: 'l_yeon' },
      { group: 'empDeduct', name: '건강보험', field: 'l_gun' },
      { group: 'empDeduct', name: '장기요양', field: 'l_jang' },
      { group: 'empDeduct', name: '고용보험', field: 'l_go' },
      { group: 'empDeduct', name: '소득세', field: 'l_so' },
      { group: 'empDeduct', name: '지방세', field: 'l_ji' },
      { group: 'empDeduct', name: '지원공제', field: 'l_jiwon' },
      { group: 'empDeduct', name: '본인부담금', field: 'l_bonin' },
      { group: 'empDeduct', name: '기타공제', field: 'l_etc' },
      { group: 'empDeduct', name: '근로자공제합계', field: 'l_total', required: true },
      { group: 'net', name: '실수령액', field: null, required: true },
      // 기관공제
      { group: 'corpDeduct', name: '국민연금', field: 'c_yeon' },
      { group: 'corpDeduct', name: '건강보험', field: 'c_gun' },
      { group: 'corpDeduct', name: '장기요양', field: 'c_jang' },
      { group: 'corpDeduct', name: '고용보험', field: 'c_go' },
      { group: 'corpDeduct', name: '실업급여', field: 'c_sil' },
      { group: 'corpDeduct', name: '산재보험', field: 'c_san' },
      { group: 'corpDeduct', name: '퇴직금', field: 'c_out' },
      { group: 'corpDeduct', name: '기관공제합계', field: 'c_total', required: true },
      { group: 'remark', name: '비고', field: null, required: true }
    ];

    const laborDayMonth = isHomeWageLaborDayMonth(
      `${props.year}${String(props.month).padStart(2, '0')}`
    );

    // 0이 아닌 값이 있는 컬럼만 포함 (표시 금액·노동절 5월만)
    const hasNonZeroValue = (field: string | null): boolean => {
      if (!field) return true;
      if (field === 'laborDay' && !laborDayMonth) return false;
      return props.wageData.some((row) => getDisplayWageField(row, field) > 0);
    };

    const activeColumns = allColumns.filter(col => col.required || hasNonZeroValue(col.field));

    // 2단 헤더 생성
    const header2Columns = activeColumns.map(col => col.name);

    // 그룹별 시작/끝 인덱스 계산
    const getGroupRange = (groupName: string) => {
      const cols = activeColumns.map((c, i) => ({ ...c, idx: i + 1 })).filter(c => c.group === groupName);
      if (cols.length === 0) return null;
      return { start: cols[0].idx, end: cols[cols.length - 1].idx };
    };

    const basicRange = getGroupRange('basic');
    const workRange = getGroupRange('work');
    const wageRange = getGroupRange('wage');
    const empDeductRange = getGroupRange('empDeduct');
    const corpDeductRange = getGroupRange('corpDeduct');
    const netRange = getGroupRange('net');
    const remarkRange = getGroupRange('remark');

    addWageLedgerTitleRow(worksheet, wageLedgerTitle(), header2Columns.length);

    // 1단 헤더 행 추가 (제목행이 1행)
    const header1Row = worksheet.addRow([]);
    if (basicRange) header1Row.getCell(basicRange.start).value = '기본정보';
    if (workRange) header1Row.getCell(workRange.start).value = '근로정보';
    if (wageRange) header1Row.getCell(wageRange.start).value = '임금구성';
    if (empDeductRange) header1Row.getCell(empDeductRange.start).value = '근로자공제';
    if (corpDeductRange) header1Row.getCell(corpDeductRange.start).value = '기관공제';
    if (netRange) header1Row.getCell(netRange.start).value = '수령';
    if (remarkRange) header1Row.getCell(remarkRange.start).value = '비고';

    // 1단 헤더 병합
    if (basicRange && basicRange.end > basicRange.start) worksheet.mergeCells(2, basicRange.start, 2, basicRange.end);
    if (workRange && workRange.end > workRange.start) worksheet.mergeCells(2, workRange.start, 2, workRange.end);
    if (wageRange && wageRange.end > wageRange.start) worksheet.mergeCells(2, wageRange.start, 2, wageRange.end);
    if (empDeductRange && empDeductRange.end > empDeductRange.start) worksheet.mergeCells(2, empDeductRange.start, 2, empDeductRange.end);
    if (corpDeductRange && corpDeductRange.end > corpDeductRange.start) worksheet.mergeCells(2, corpDeductRange.start, 2, corpDeductRange.end);

    // 1단 헤더 스타일
    for (let i = 1; i <= header2Columns.length; i++) {
      const cell = header1Row.getCell(i);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1e3a5f' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }

    // 2단 헤더 행 추가
    const header2Row = worksheet.addRow(header2Columns);
    header2Row.eachCell((cell, colNumber) => {
      let bgColor = 'FFD9E1F2';
      if (wageRange && colNumber >= wageRange.start && colNumber <= wageRange.end) bgColor = 'FFE2EFDA';
      if (empDeductRange && colNumber >= empDeductRange.start && colNumber <= empDeductRange.end) bgColor = 'FFFCE4D6';
      if (corpDeductRange && colNumber >= corpDeductRange.start && colNumber <= corpDeductRange.end) bgColor = 'FFFFF2CC';
      
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // 열 너비 설정
    worksheet.getColumn(1).width = 6;
    worksheet.getColumn(2).width = 10;
    worksheet.getColumn(3).width = 12;
    worksheet.getColumn(4).width = 12;
    for (let i = 5; i <= header2Columns.length; i++) {
      const colName = header2Columns[i - 1];
      if (colName.includes('합계') || colName === '실수령액' || colName === '연야휴과세' || colName === '연야휴비과세') worksheet.getColumn(i).width = 13;
      else if (colName === '비고') worksheet.getColumn(i).width = 10;
      else if (colName === '기본급') worksheet.getColumn(i).width = 12;
      else if (colName.includes('중식비')) worksheet.getColumn(i).width = 14;
      else worksheet.getColumn(i).width = 10;
    }

    // 합계 변수
    const totals: Record<string, number> = {};
    activeColumns.forEach(col => { 
      if (col.field || col.name === '실수령액' || col.name === '임금합계') totals[excelColTotalKey(col)] = 0; 
    });

    // 데이터 행 추가
    sortedData.value.forEach((row, index) => {
      const grossPay = getReportRowGrossPay(row);
      const netPay = getRowNetPay(row);
      const remarkText = calculateAge(row.birth, props.year, props.month);

      const rowData = activeColumns.map((col) => {
        if (col.name === '순번') return index + 1;
        if (col.name === '성명') return row.name || '';
        if (col.name === '생년월일') return row.birth ? row.birth.substring(0, 10) : '';
        if (col.name === '직종') return row.job || '';
        if (col.name === '임금합계') return grossPay;
        if (col.name === '실수령액') return netPay;
        if (col.name === '비고') return remarkText;
        if (col.field === 'hour') return formatWorkHoursCell(row.hour) || '0';
        if (col.field) return getDisplayWageField(row, col.field);
        return '';
      });

      const dataRow = worksheet.addRow(rowData);
      const basicEnd = basicRange?.end || 4;
      const remarkStart = remarkRange?.start || activeColumns.length;
      
      dataRow.eachCell((cell, colNumber) => {
        const col = activeColumns[colNumber - 1];
        cell.alignment = { horizontal: colNumber <= basicEnd ? 'center' : 'right', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        if (colNumber > basicEnd && colNumber < remarkStart && col?.field !== 'hour') {
          cell.numFmt = '#,##0';
        }
        // 합계 누적
        if (col?.field && col.field !== 'hour') {
          const key = excelColTotalKey(col);
          totals[key] = (totals[key] || 0) + getDisplayWageField(row, col.field);
        }
        if (col?.name === '임금합계') totals['임금합계'] = (totals['임금합계'] || 0) + grossPay;
        if (col?.name === '실수령액') totals['실수령액'] = (totals['실수령액'] || 0) + netPay;
      });
    });

    // 합계 행
    const totalRowData = activeColumns.map((col) => {
      if (col.name === '순번') return '';
      if (col.name === '성명') return '합계';
      if (col.name === '생년월일' || col.name === '직종' || col.name === '비고') return '';
      return totals[excelColTotalKey(col)] || 0;
    });

    const totalRow = worksheet.addRow(totalRowData);
    const basicEnd = basicRange?.end || 4;
    const remarkStart = remarkRange?.start || activeColumns.length;
    totalRow.eachCell((cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: colNumber <= basicEnd ? 'center' : 'right', vertical: 'middle' };
      cell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      if (colNumber > basicEnd && colNumber < remarkStart) {
        cell.numFmt = '#,##0';
      }
    });

    // 파일 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = formatReportFileName(props.memberName, props.year, props.month, 'accounting');
    saveAs(blob, fileName);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : '알 수 없는 오류';
    console.error('엑셀 다운로드 오류:', error);
    emit('error', `임금대장 다운로드 중 오류가 발생했습니다: ${message}`);
  } finally {
    isDownloading.value = false;
  }
};

/** 임금대장(합산) 엑셀 다운로드 — 과세+비과세 합산 */
const downloadClientReport = async () => {
  if (!props.wageData || props.wageData.length === 0) {
    emit('error', '다운로드할 데이터가 없습니다.');
    return;
  }

  isDownloading.value = true;
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('임금대장');

    type ColDef = { group: string; name: string; field: string | null; required?: boolean };
    const allColumns: ColDef[] = [
      { group: 'basic', name: '순번', field: null, required: true },
      { group: 'basic', name: '성명', field: null, required: true },
      { group: 'basic', name: '생년월일', field: null, required: true },
      { group: 'basic', name: '직종', field: null, required: true },
      { group: 'work', name: '시급', field: 'payCalcHourly', required: true },
      { group: 'work', name: '일수', field: 'days', required: true },
      { group: 'work', name: '시간', field: 'hour', required: true },
      { group: 'wage', name: '기본급', field: 'basic', required: true },
      { group: 'wage', name: '주휴수당', field: 'weekend' },
      { group: 'wage', name: '연차수당', field: 'annual' },
      { group: 'wage', name: '노동절수당(비과세)', field: 'laborDay' },
      { group: 'wage', name: '연장수당', field: '_overtime_total' },
      { group: 'wage', name: '야간수당', field: '_night_total' },
      { group: 'wage', name: '휴일수당', field: '_holiday_total' },
      { group: 'wage', name: '장기근속', field: 'senior' },
      { group: 'wage', name: '보수교육', field: 'bosu' },
      { group: 'wage', name: '중증가산', field: 'severe' },
      { group: 'wage', name: '상여금', field: 'bonus' },
      { group: 'wage', name: HOME_WAGE_NONTAX_TERM.lunch, field: 'lunch' },
      { group: 'wage', name: HOME_WAGE_NONTAX_TERM.car, field: 'car' },
      { group: 'wage', name: HOME_WAGE_NONTAX_TERM.child, field: 'child' },
      { group: 'wage', name: '처우개선', field: 'welfare' },
      { group: 'wage', name: '기타수당', field: 'other' },
      { group: 'wage', name: '임금합계', field: null, required: true },
      { group: 'empDeduct', name: '국민연금', field: 'l_yeon' },
      { group: 'empDeduct', name: '건강보험', field: 'l_gun' },
      { group: 'empDeduct', name: '장기요양', field: 'l_jang' },
      { group: 'empDeduct', name: '고용보험', field: 'l_go' },
      { group: 'empDeduct', name: '소득세', field: 'l_so' },
      { group: 'empDeduct', name: '지방세', field: 'l_ji' },
      { group: 'empDeduct', name: '지원공제', field: 'l_jiwon' },
      { group: 'empDeduct', name: '본인부담금', field: 'l_bonin' },
      { group: 'empDeduct', name: '기타공제', field: 'l_etc' },
      { group: 'empDeduct', name: '근로자공제합계', field: 'l_total', required: true },
      { group: 'net', name: '실수령액', field: null, required: true },
      { group: 'corpDeduct', name: '국민연금', field: 'c_yeon' },
      { group: 'corpDeduct', name: '건강보험', field: 'c_gun' },
      { group: 'corpDeduct', name: '장기요양', field: 'c_jang' },
      { group: 'corpDeduct', name: '고용보험', field: 'c_go' },
      { group: 'corpDeduct', name: '실업급여', field: 'c_sil' },
      { group: 'corpDeduct', name: '산재보험', field: 'c_san' },
      { group: 'corpDeduct', name: '퇴직금', field: 'c_out' },
      { group: 'corpDeduct', name: '기관공제합계', field: 'c_total', required: true },
      { group: 'remark', name: '비고', field: null, required: true },
    ];

    const getFieldValue = (row: HomeWageListRow, field: string | null): number => {
      if (!field) return 0;
      return getDisplayWageField(row, field);
    };

    const laborDayMonthClient = isHomeWageLaborDayMonth(
      `${props.year}${String(props.month).padStart(2, '0')}`
    );

    const hasNonZeroValue = (field: string | null): boolean => {
      if (!field) return true;
      if (field === 'laborDay') {
        if (!laborDayMonthClient) return false;
        return props.wageData.some((row) => getDisplayLaborDay(row) > 0);
      }
      if (field === 'annual') {
        return props.wageData.some((row) => getFieldValue(row, 'annual') > 0);
      }
      if (field === 'basic') return true;
      return props.wageData.some((row) => getFieldValue(row, field) > 0);
    };

    const activeColumns = allColumns.filter((col) => col.required || hasNonZeroValue(col.field));
    const header2Columns = activeColumns.map((col) => col.name);

    const getGroupRange = (groupName: string) => {
      const cols = activeColumns.map((c, i) => ({ ...c, idx: i + 1 })).filter((c) => c.group === groupName);
      if (cols.length === 0) return null;
      return { start: cols[0].idx, end: cols[cols.length - 1].idx };
    };

    const basicRange = getGroupRange('basic');
    const workRange = getGroupRange('work');
    const wageRange = getGroupRange('wage');
    const empDeductRange = getGroupRange('empDeduct');
    const corpDeductRange = getGroupRange('corpDeduct');
    const netRange = getGroupRange('net');
    const remarkRange = getGroupRange('remark');

    addWageLedgerTitleRow(worksheet, wageLedgerTitle(), header2Columns.length);

    const header1Row = worksheet.addRow([]);
    if (basicRange) header1Row.getCell(basicRange.start).value = '기본정보';
    if (workRange) header1Row.getCell(workRange.start).value = '근로정보';
    if (wageRange) header1Row.getCell(wageRange.start).value = '임금구성';
    if (empDeductRange) header1Row.getCell(empDeductRange.start).value = '근로자공제';
    if (corpDeductRange) header1Row.getCell(corpDeductRange.start).value = '기관공제';
    if (netRange) header1Row.getCell(netRange.start).value = '수령';
    if (remarkRange) header1Row.getCell(remarkRange.start).value = '비고';

    if (basicRange && basicRange.end > basicRange.start) {
      worksheet.mergeCells(2, basicRange.start, 2, basicRange.end);
    }
    if (workRange && workRange.end > workRange.start) {
      worksheet.mergeCells(2, workRange.start, 2, workRange.end);
    }
    if (wageRange && wageRange.end > wageRange.start) {
      worksheet.mergeCells(2, wageRange.start, 2, wageRange.end);
    }
    if (empDeductRange && empDeductRange.end > empDeductRange.start) {
      worksheet.mergeCells(2, empDeductRange.start, 2, empDeductRange.end);
    }
    if (corpDeductRange && corpDeductRange.end > corpDeductRange.start) {
      worksheet.mergeCells(2, corpDeductRange.start, 2, corpDeductRange.end);
    }

    for (let i = 1; i <= header2Columns.length; i++) {
      const cell = header1Row.getCell(i);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1e3a5f' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    }

    const header2Row = worksheet.addRow(header2Columns);
    header2Row.eachCell((cell, colNumber) => {
      let bgColor = 'FFD9E1F2';
      if (wageRange && colNumber >= wageRange.start && colNumber <= wageRange.end) bgColor = 'FFE2EFDA';
      if (empDeductRange && colNumber >= empDeductRange.start && colNumber <= empDeductRange.end) {
        bgColor = 'FFFCE4D6';
      }
      if (corpDeductRange && colNumber >= corpDeductRange.start && colNumber <= corpDeductRange.end) {
        bgColor = 'FFFFF2CC';
      }

      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    worksheet.getColumn(1).width = 6;
    worksheet.getColumn(2).width = 10;
    worksheet.getColumn(3).width = 12;
    worksheet.getColumn(4).width = 12;
    for (let i = 5; i <= header2Columns.length; i++) {
      const colName = header2Columns[i - 1];
      if (colName.includes('합계') || colName === '실수령액' || colName === '연야휴과세' || colName === '연야휴비과세') worksheet.getColumn(i).width = 13;
      else if (colName === '비고') worksheet.getColumn(i).width = 10;
      else if (colName === '기본급') worksheet.getColumn(i).width = 12;
      else if (colName.includes('중식비')) worksheet.getColumn(i).width = 14;
      else worksheet.getColumn(i).width = 10;
    }

    const totalsMap: Record<string, number> = {};
    activeColumns.forEach((col) => {
      if (col.field || col.name === '실수령액' || col.name === '임금합계') totalsMap[excelColTotalKey(col)] = 0;
    });

    sortedData.value.forEach((row, index) => {
      const grossPay = getReportRowGrossPay(row);
      const netPay = getRowNetPay(row);
      const remarkText = calculateAge(row.birth, props.year, props.month);

      const rowData = activeColumns.map((col) => {
        if (col.name === '순번') return index + 1;
        if (col.name === '성명') return row.name || '';
        if (col.name === '생년월일') return row.birth ? row.birth.substring(0, 10) : '';
        if (col.name === '직종') return row.job || '';
        if (col.name === '임금합계') return grossPay;
        if (col.name === '실수령액') return netPay;
        if (col.name === '비고') return remarkText;
        if (col.field === 'hour') return formatWorkHoursCell(row.hour) || '0';
        if (col.field) return getFieldValue(row, col.field);
        return '';
      });

      const dataRow = worksheet.addRow(rowData);
      const basicEnd = basicRange?.end || 4;
      const remarkStart = remarkRange?.start || activeColumns.length;

      dataRow.eachCell((cell, colNumber) => {
        const col = activeColumns[colNumber - 1];
        cell.alignment = {
          horizontal: colNumber <= basicEnd ? 'center' : 'right',
          vertical: 'middle',
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if (colNumber > basicEnd && colNumber < remarkStart && col?.field !== 'hour') {
          cell.numFmt = '#,##0';
        }
        if (col?.field && col.field !== 'hour') {
          const key = excelColTotalKey(col);
          totalsMap[key] = (totalsMap[key] || 0) + getFieldValue(row, col.field);
        }
        if (col?.name === '임금합계') totalsMap['임금합계'] = (totalsMap['임금합계'] || 0) + grossPay;
        if (col?.name === '실수령액') totalsMap['실수령액'] = (totalsMap['실수령액'] || 0) + netPay;
      });
    });

    const totalRowData = activeColumns.map((col) => {
      if (col.name === '순번') return '';
      if (col.name === '성명') return '합계';
      if (col.name === '생년월일' || col.name === '직종' || col.name === '비고') return '';
      return totalsMap[excelColTotalKey(col)] || 0;
    });

    const totalRow = worksheet.addRow(totalRowData);
    const basicEnd = basicRange?.end || 4;
    const remarkStart = remarkRange?.start || activeColumns.length;
    totalRow.eachCell((cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: colNumber <= basicEnd ? 'center' : 'right', vertical: 'middle' };
      cell.border = {
        top: { style: 'medium' },
        left: { style: 'thin' },
        bottom: { style: 'medium' },
        right: { style: 'thin' },
      };
      if (colNumber > basicEnd && colNumber < remarkStart) {
        cell.numFmt = '#,##0';
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, formatReportFileName(props.memberName, props.year, props.month));
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : '알 수 없는 오류';
    console.error('임금대장 다운로드 오류:', error);
    emit('error', `임금대장 다운로드 중 오류가 발생했습니다: ${message}`);
  } finally {
    isDownloading.value = false;
  }
};

defineExpose({
  downloadPayrollReport,
  downloadClientReport,
  openPreview,
});
</script>

<template>
  <Teleport to="body">
    <div v-if="showModal" class="home-wage-report-modal-overlay" @click.self="closePreview">
      <div class="home-wage-report-modal">
        <div class="modal-header">
          <h3>{{ reportModalTitle }}</h3>
          <div class="header-actions">
            <button class="download-btn" @click="handleDownload" :disabled="isDownloading">
              <v-icon v-if="isDownloading" size="16" class="spin">mdi-loading</v-icon>
              <v-icon v-else size="16">mdi-download</v-icon>
              {{ isDownloading ? '다운로드 중...' : '엑셀 다운로드' }}
            </button>
            <button class="close-btn" @click="closePreview">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="modal-body">
          <div class="table-wrapper">
            <table class="home-wage-report-table">
              <thead>
                <tr class="header-row-1">
                  <th colspan="4" class="section-basic">기본정보</th>
                  <th colspan="3" class="section-work">근로정보</th>
                  <th :colspan="wageColspan" class="section-wage">임금구성</th>
                  <th :colspan="empDeductColspan" class="section-emp-deduct">근로자공제</th>
                  <th class="section-net">수령</th>
                  <th :colspan="corpDeductColspan" class="section-corp-deduct">기관공제</th>
                </tr>
                <tr class="header-row-2">
                  <th>순번</th>
                  <th>성명</th>
                  <th>생년월일</th>
                  <th>직종</th>
                  <th>시급</th>
                  <th>일수</th>
                  <th>시간</th>
                  <th>기본급</th>
                  <th v-if="showColumns.weekend">주휴</th>
                  <th v-if="showColumns.annual">연차</th>
                  <th v-if="showColumns.laborDay">노동절</th>
                  <template v-if="reportType === 'accounting'">
                    <th v-if="showColumns.premiumTax">연야휴과세</th>
                    <th v-if="showColumns.premiumNontax" class="nontax-col">연야휴비과세</th>
                  </template>
                  <template v-else>
                    <th v-if="showClientColumns.overtimeTotal">연장</th>
                    <th v-if="showClientColumns.nightTotal">야간</th>
                    <th v-if="showClientColumns.holidayTotal">휴일</th>
                  </template>
                  <th v-if="showColumns.senior">장기</th>
                  <th v-if="showColumns.sunim">선임</th>
                  <th v-if="showColumns.bosu">보수</th>
                  <th v-if="showColumns.severe">중증</th>
                  <th v-if="showColumns.bonus">상여</th>
                  <th v-if="showColumns.lunch">{{ HOME_WAGE_NONTAX_TERM.lunch }}</th>
                  <th v-if="showColumns.car">{{ HOME_WAGE_NONTAX_TERM.car }}</th>
                  <th v-if="showColumns.child">{{ HOME_WAGE_NONTAX_TERM.child }}</th>
                  <th v-if="showColumns.welfare">처우</th>
                  <th v-if="showColumns.other">기타</th>
                  <th class="total-col">임금합계</th>
                  <th v-if="showColumns.lYeon">국민연금</th>
                  <th v-if="showColumns.lGun">건강보험</th>
                  <th v-if="showColumns.lJang">장기요양</th>
                  <th v-if="showColumns.lGo">고용보험</th>
                  <th v-if="showColumns.lSo">소득세</th>
                  <th v-if="showColumns.lJi">지방세</th>
                  <th v-if="showColumns.lJiwon">지원공제</th>
                  <th v-if="showColumns.lBonin">본인부담</th>
                  <th v-if="showColumns.lEtc">기타공제</th>
                  <th class="total-col">공제합계</th>
                  <th class="net-col">실수령액</th>
                  <th v-if="showColumns.cYeon">국민연금</th>
                  <th v-if="showColumns.cGun">건강보험</th>
                  <th v-if="showColumns.cJang">장기요양</th>
                  <th v-if="showColumns.cGo">고용보험</th>
                  <th v-if="showColumns.cSil">실업급여</th>
                  <th v-if="showColumns.cSan">산재보험</th>
                  <th v-if="showColumns.cOut">퇴직금</th>
                  <th class="total-col">기관합계</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in sortedData" :key="`${row.name}|${row.birth}`" class="data-row">
                  <td class="center">{{ index + 1 }}</td>
                  <td class="center">{{ row.name }}</td>
                  <td class="center">{{ row.birth?.substring(0, 10) }}</td>
                  <td class="center">{{ row.job }}</td>
                  <td class="right">{{ formatNumber(row.payCalcHourly) }}</td>
                  <td class="right">{{ row.days }}</td>
                  <td class="right">{{ formatWorkHoursCell(row.hour) }}</td>
                  <td class="right">{{ formatNumber(getDisplayBasic(row)) }}</td>
                  <td v-if="showColumns.weekend" class="right">{{ formatNumber(getDisplayWeekend(row)) }}</td>
                  <td v-if="showColumns.annual" class="right">{{ formatNumber(getDisplayAnnual(row)) }}</td>
                  <td v-if="showColumns.laborDay" class="right">{{ formatNumber(getDisplayLaborDay(row)) }}</td>
                  <template v-if="reportType === 'accounting'">
                    <td v-if="showColumns.premiumTax" class="right">{{ formatNumber(getAccountingPremiumTax(row)) }}</td>
                    <td v-if="showColumns.premiumNontax" class="right nontax-col">{{ formatNumber(getAccountingPremiumNontax(row)) }}</td>
                  </template>
                  <template v-else>
                    <td v-if="showClientColumns.overtimeTotal" class="right">{{ formatNumber(getDisplayOvertimeTotal(row)) }}</td>
                    <td v-if="showClientColumns.nightTotal" class="right">{{ formatNumber(getDisplayNight(row)) }}</td>
                    <td v-if="showClientColumns.holidayTotal" class="right">{{ formatNumber(getDisplayHoliday(row)) }}</td>
                  </template>
                  <td v-if="showColumns.senior" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'senior') : getAdminParts(row).senior) }}</td>
                  <td v-if="showColumns.sunim" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'sunim') : getAdminParts(row).sunim) }}</td>
                  <td v-if="showColumns.bosu" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'bosu') : getAdminParts(row).bosu) }}</td>
                  <td v-if="showColumns.severe" class="right">{{ formatNumber(getDisplaySevere(row)) }}</td>
                  <td v-if="showColumns.bonus" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'bonus') : getAdminParts(row).bonus) }}</td>
                  <td v-if="showColumns.lunch" class="right">{{ formatNumber(getDisplayLunch(row)) }}</td>
                  <td v-if="showColumns.car" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'car') : getAdminParts(row).car) }}</td>
                  <td v-if="showColumns.child" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'child') : getAdminParts(row).child) }}</td>
                  <td v-if="showColumns.welfare" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'welfare') : getAdminParts(row).welfare) }}</td>
                  <td v-if="showColumns.other" class="right">{{ formatNumber(isClientReport() ? getClientAllowance(row, 'other') : getAdminParts(row).other) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': rowWageMismatch(row) }"
                    :title="rowWageMismatch(row) ? '항목 합과 임금합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(getReportRowGrossPay(row)) }}</td>
                  <td v-if="showColumns.lYeon" class="right">{{ formatNumber(row.l_yeon) }}</td>
                  <td v-if="showColumns.lGun" class="right">{{ formatNumber(row.l_gun) }}</td>
                  <td v-if="showColumns.lJang" class="right">{{ formatNumber(row.l_jang) }}</td>
                  <td v-if="showColumns.lGo" class="right">{{ formatNumber(row.l_go) }}</td>
                  <td v-if="showColumns.lSo" class="right">{{ formatNumber(row.l_so) }}</td>
                  <td v-if="showColumns.lJi" class="right">{{ formatNumber(row.l_ji) }}</td>
                  <td v-if="showColumns.lJiwon" class="right">{{ formatNumber(row.l_jiwon) }}</td>
                  <td v-if="showColumns.lBonin" class="right">{{ formatNumber(row.l_bonin) }}</td>
                  <td v-if="showColumns.lEtc" class="right">{{ formatNumber(row.l_etc) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': rowLaborMismatch(row) }"
                    :title="rowLaborMismatch(row) ? '근로자공제 항목 합과 공제합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(row.l_total) }}</td>
                  <td class="right net-col">{{ formatNumber(getRowNetPay(row)) }}</td>
                  <td v-if="showColumns.cYeon" class="right">{{ formatNumber(row.c_yeon) }}</td>
                  <td v-if="showColumns.cGun" class="right">{{ formatNumber(row.c_gun) }}</td>
                  <td v-if="showColumns.cJang" class="right">{{ formatNumber(row.c_jang) }}</td>
                  <td v-if="showColumns.cGo" class="right">{{ formatNumber(row.c_go) }}</td>
                  <td v-if="showColumns.cSil" class="right">{{ formatNumber(row.c_sil) }}</td>
                  <td v-if="showColumns.cSan" class="right">{{ formatNumber(row.c_san) }}</td>
                  <td v-if="showColumns.cOut" class="right">{{ formatNumber(getReportRetirement(row)) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': rowCorpMismatch(row) }"
                    :title="rowCorpMismatch(row) ? '기관공제 항목 합과 기관합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(getReportCorpTotal(row)) }}</td>
                </tr>
                <tr class="total-row">
                  <td class="center" colspan="4">합계</td>
                  <td class="right" colspan="3"></td>
                  <td class="right">{{ formatNumber(displayBasicTotal) }}</td>
                  <td v-if="showColumns.weekend" class="right">{{ formatNumber(totals.weekend) }}</td>
                  <td v-if="showColumns.annual" class="right">{{ formatNumber(displayAnnualTotal) }}</td>
                  <td v-if="showColumns.laborDay" class="right">{{ formatNumber(totals.laborDay) }}</td>
                  <template v-if="reportType === 'accounting'">
                    <td v-if="showColumns.premiumTax" class="right">{{ formatNumber(totals.premiumTax) }}</td>
                    <td v-if="showColumns.premiumNontax" class="right nontax-col">{{ formatNumber(totals.premiumNontax) }}</td>
                  </template>
                  <template v-else>
                    <td v-if="showClientColumns.overtimeTotal" class="right">{{ formatNumber(displayOvertimeTotal) }}</td>
                    <td v-if="showClientColumns.nightTotal" class="right">{{ formatNumber(displayNightTotal) }}</td>
                    <td v-if="showClientColumns.holidayTotal" class="right">{{ formatNumber(displayHolidayTotal) }}</td>
                  </template>
                  <td v-if="showColumns.senior" class="right">{{ formatNumber(totals.senior) }}</td>
                  <td v-if="showColumns.sunim" class="right">{{ formatNumber(totals.sunim) }}</td>
                  <td v-if="showColumns.bosu" class="right">{{ formatNumber(totals.bosu) }}</td>
                  <td v-if="showColumns.severe" class="right">{{ formatNumber(totals.severe) }}</td>
                  <td v-if="showColumns.bonus" class="right">{{ formatNumber(totals.bonus) }}</td>
                  <td v-if="showColumns.lunch" class="right">{{ formatNumber(totals.lunch) }}</td>
                  <td v-if="showColumns.car" class="right">{{ formatNumber(totals.car) }}</td>
                  <td v-if="showColumns.child" class="right">{{ formatNumber(totals.child) }}</td>
                  <td v-if="showColumns.welfare" class="right">{{ formatNumber(totals.welfare) }}</td>
                  <td v-if="showColumns.other" class="right">{{ formatNumber(totals.other) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': footerMismatch.wage }"
                    :title="footerMismatch.wage ? '임금 항목 합과 임금합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(totals.grossPay) }}</td>
                  <td v-if="showColumns.lYeon" class="right">{{ formatNumber(totals.lYeon) }}</td>
                  <td v-if="showColumns.lGun" class="right">{{ formatNumber(totals.lGun) }}</td>
                  <td v-if="showColumns.lJang" class="right">{{ formatNumber(totals.lJang) }}</td>
                  <td v-if="showColumns.lGo" class="right">{{ formatNumber(totals.lGo) }}</td>
                  <td v-if="showColumns.lSo" class="right">{{ formatNumber(totals.lSo) }}</td>
                  <td v-if="showColumns.lJi" class="right">{{ formatNumber(totals.lJi) }}</td>
                  <td v-if="showColumns.lJiwon" class="right">{{ formatNumber(totals.lJiwon) }}</td>
                  <td v-if="showColumns.lBonin" class="right">{{ formatNumber(totals.lBonin) }}</td>
                  <td v-if="showColumns.lEtc" class="right">{{ formatNumber(totals.lEtc) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': footerMismatch.labor }"
                    :title="footerMismatch.labor ? '근로자공제 합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(totals.lTotal) }}</td>
                  <td class="right net-col">{{ formatNumber(totals.netPay) }}</td>
                  <td v-if="showColumns.cYeon" class="right">{{ formatNumber(totals.cYeon) }}</td>
                  <td v-if="showColumns.cGun" class="right">{{ formatNumber(totals.cGun) }}</td>
                  <td v-if="showColumns.cJang" class="right">{{ formatNumber(totals.cJang) }}</td>
                  <td v-if="showColumns.cGo" class="right">{{ formatNumber(totals.cGo) }}</td>
                  <td v-if="showColumns.cSil" class="right">{{ formatNumber(totals.cSil) }}</td>
                  <td v-if="showColumns.cSan" class="right">{{ formatNumber(totals.cSan) }}</td>
                  <td v-if="showColumns.cOut" class="right">{{ formatNumber(totals.cOut) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': footerMismatch.corp }"
                    :title="footerMismatch.corp ? '기관공제 합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(totals.cTotal) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.home-wage-report-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-wage-report-modal {
  background: white;
  border-radius: 8px;
  width: 95vw;
  height: 85vh;
  max-height: 85vh;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 8px 8px 0 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .download-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: #10b981;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #059669;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }

    .spin {
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    color: #374151;

    &:hover {
      background: #e5e7eb;
      color: #111827;
    }
  }
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.home-wage-report-table {
  width: max-content;
  border-collapse: collapse;
  font-size: 11px;

  th, td {
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  .header-row-1 th {
    background: #1e3a5f;
    color: white;
    font-weight: 600;
    text-align: center;
    padding: 8px;
    position: sticky;
    top: 0;
    z-index: 3;
  }

  .header-row-2 th {
    background: #e5e7eb;
    font-weight: 600;
    text-align: center;
    position: sticky;
    top: 33px;
    z-index: 2;
  }

  .section-basic { background: #1e3a5f; }
  .section-work { background: #2563eb; }
  .section-wage { background: #059669; }
  .section-emp-deduct { background: #dc2626; }
  .section-corp-deduct { background: #d97706; }
  .section-net { background: #0891b2; }

  .total-col {
    background: #fef3c7 !important;
    font-weight: 600;
  }

  .total-mismatch {
    color: #c62828 !important;
    font-weight: 700;
  }

  .net-col {
    background: #dcfce7 !important;
    font-weight: 600;
    color: #166534;
  }

  .data-row {
    td {
      background: white;
    }

    &:hover td {
      background: #f9fafb;
    }
  }

  .total-row {
    td {
      background: #fef3c7 !important;
      font-weight: 600;
      border-top: 2px solid #1e3a5f;
    }
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }
}
</style>
